import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Platform, Dimensions } from 'react-native';
import { db, auth } from '../firebaseConfig';
import { collection, addDoc, getDocs, query, orderBy, limit, where, serverTimestamp, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { runSearchPipeline, hydrateResultsForRender } from '../services/searchPipeline';
import { countBackgroundQuery } from '../utils/resultCounts';

// Preferences that change the Background Queries counts; a query is recounted only when these change.
const countsKey = (profile) => {
  const p = profile?.preferences || {};
  return JSON.stringify([p.myBooks || [], p.minBooksStraights, p.minBooksSGP, p.minBooksMyBooks,
    p.defaultBookSort, p.filterPositiveEvOnly, p.consensusBookPolicy, p.consensusExcludedBooks || []]);
};
import Constants from 'expo-constants';
import { useAuth } from './AuthContext';

const BackgroundQueryContext = createContext();

const DEFAULT_BG_LIMIT = 3;

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
);

// Screen size detection
const { width: SCREEN_WIDTH } = Dimensions.get('window');
const IS_MOBILE = SCREEN_WIDTH < 768;

// Query statuses
export const QueryStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error',
  CANCELLED: 'cancelled'
};

export function BackgroundQueryProvider({ children }) {
  const { userProfile } = useAuth();

  // Active background queries (up to 3)
  const [activeQueries, setActiveQueries] = useState([]);
  
  // Query history (loaded from Firestore)
  const [queryHistory, setQueryHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Minimized state for the modal
  const [isMinimized, setIsMinimized] = useState(IS_MOBILE); // Auto-minimize on mobile
  
  // Position state: 'bottom-right' | 'bottom-left'
  const [modalPosition, setModalPosition] = useState('bottom-right');
  
  // Mobile-specific: completely hidden vs showing indicator
  const [isMobileHidden, setIsMobileHidden] = useState(false);
  
  // In-memory map of queryId → top-100 results by clean EV (never persisted, rolling window of 50 entries)
  const recentResultsMap = useRef(new Map());

  // Track which completed queries have been viewed
  const [viewedCompletedQueries, setViewedCompletedQueries] = useState(new Set());
  
  // Notification sound ref
  const notificationSoundRef = useRef(null);
  
  // Track foreground query that can be converted to background
  const [foregroundQuery, setForegroundQuery] = useState(null);

  // Latest-value refs. SgpFlow calls the foreground/background callbacks from
  // closures made before its own state commits (executeQuery) and from an
  // unmount cleanup captured at mount, so they must read current state rather
  // than the render they were created in. `latestRef` is refreshed every render
  // below, once processQuery / saveToHistory exist.
  const foregroundQueryRef = useRef(null);
  const latestRef = useRef({});
  const setForeground = useCallback((next) => {
    foregroundQueryRef.current = next;
    setForegroundQuery(next);
  }, []);

  // Config-driven background query limit (reads from HTTP endpoint, user info from AuthContext)
  const [maxConcurrentQueries, setMaxConcurrentQueries] = useState(DEFAULT_BG_LIMIT);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = getBase();
        if (!base) return;
        const res = await fetch(`${base}/getSubscriptionConfig`);
        const json = await res.json();
        if (cancelled || !json.success || !json.data) return;
        const cfg = json.data;

        let bgLimit = DEFAULT_BG_LIMIT;
        const isAdmin = userProfile?.userType === 'admin';
        if (isAdmin && cfg.userTypes?.admin?.backgroundQueryLimit) {
          bgLimit = cfg.userTypes.admin.backgroundQueryLimit;
        } else {
          const planKey = userProfile?.subscription?.planType?.toUpperCase() || 'FREE';
          bgLimit = cfg.plans?.[planKey]?.backgroundQueryLimit ?? DEFAULT_BG_LIMIT;
        }
        if (!cancelled) setMaxConcurrentQueries(bgLimit);
      } catch (e) {
        console.warn('[BackgroundQuery] Failed to load config, using default limit:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [userProfile?.userType, userProfile?.subscription?.planType]);

  // Generate unique query ID
  const generateQueryId = () => `query_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Guard against double-invocation from React Strict Mode — two concurrent getDocs
  // on the same collection path collide on a Firestore Target ID.
  const historyLoadInFlight = useRef(false);

  // ── Recompute All Books / My Books counts when profile loads or queries change ──
  // Fires when: (a) myBooks preference changes, (b) a new query is added to the list.
  // This corrects two scenarios:
  //   1. Ticker queries that complete before userProfile loads (hasMyBooks=false, count=0)
  //   2. Queries processed with old JS builds where booksCalculated = all_books.length
  //      instead of the server-stamped value. Math.max(r.booksCalculated, r.raw.booksCalculated)
  //      recovers the correct count from the raw API item that's always preserved.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const myBooks = (userProfile?.preferences?.myBooks || []);
    // Every user gets the page's counts (Oct 1, 2026: users without My Books kept
    // the search-time unique-selection count, a different unit from the page).
    const minBooks = parseInt(userProfile?.preferences?.minBooksStraights, 10) || 5;
    const minSGP   = parseInt(userProfile?.preferences?.minBooksSGP,       10) || 5;
    const userMinMyBooks = parseInt(userProfile?.preferences?.minBooksMyBooks, 10) || 3;
    const minMyBooks = Math.min(userMinMyBooks, myBooks.length);
    const myBooksLower = myBooks.map(b => (b || '').toLowerCase());
    const isMyBook = (bk) => {
      const bl = (bk || '').toLowerCase();
      return myBooksLower.some(mb => bl === mb || bl.includes(mb) || mb.includes(bl));
    };
    const baseMarket = (m) =>
      String(m || '').replace(/\s+(Alt|Alternate|Over\/Under)\b/gi, '').trim().toLowerCase();

    setActiveQueries(prev => prev.map(q => {
      if (q.status !== QueryStatus.COMPLETED) return q;
      if (!q.results?.length) return q;
      // Same counts the results page will show (utils/resultCounts). Each run is
      // ~0.2s for 500 results, so a query is recounted only when the preferences
      // that affect counts changed, not every time a query is added.
      const key = countsKey(userProfile);
      if (q._countsKey === key) return q;
      try {
        const counts = countBackgroundQuery(q, userProfile, { hydrate: hydrateResultsForRender });
        return {
          ...q,
          _countsKey: key,
          hasMyBooks: counts.hasMyBooks,
          allBooksCount: counts.allBooksCount,
          myBooksCount: counts.myBooksCount,
          allBooksCards: counts.allBooksCards,
          myBooksCards: counts.myBooksCards,
          hiddenText: counts.hiddenText,
          unreliableOnlyBooks: counts.unreliableOnlyBooks,
          relaxMinBooks: counts.relaxMinBooks,
          rawResultCount: q.rawResultCount ?? q.results.length,
        };
      } catch (countErr) {
        console.warn('[BackgroundQuery] page-count recompute failed, using card counts:', countErr?.message);
      }
      if (myBooks.length === 0) return q; // the legacy fallback below needs My Books
      // ⭐ v1.43.37: Always recompute — old JS builds stored r.booksCalculated =
      // all_books.length (1-4) instead of the server-stamped value (≥5). Recomputing
      // via Math.max(r.booksCalculated, r.raw.booksCalculated) corrects stale counts
      // for any query processed before the fix was loaded. Fast and idempotent.
      // Recompute counts from stored results
      const allSeen = new Set(), mySeen = new Set(), rawSeen = new Set();
      let allCount = 0, myCount = 0, rawCount = 0;
      for (const r of q.results) {
        if (r?._noOddsYet) continue;
        const isSGP = (r?.leg_count || r?.raw?.leg_count || 0) > 1 || !!r?._fromSGPCache;
        let cardKey;
        if (isSGP) {
          cardKey = `sgp:${r.id || r.raw?.id || Math.random()}`;
        } else {
          const sel  = r.raw?.selection || r.raw?.name || r.raw?.player_name || r.player_name || r.bet || '';
          const mkt  = baseMarket(r.market || r.raw?.market || '');
          const game = r.raw?.fixture_id || r.raw?.game || r.game || '';
          cardKey = `${game}|${sel}|${mkt}`;
        }
        const bo = r?.bookOdds || r?.raw?.bookOdds || {};
        const boKeys = Object.keys(bo);
        // ⭐ v1.43.36: Take MAX of both booksCalculated sources.
        // searchPipeline.js sets r.booksCalculated; the original API item at r.raw
        // has the server-stamped value (≥5 for every api_priced SGP).  Using max()
        // ensures we always pick the highest-quality signal regardless of which
        // transformation path ran.  Straight bets keep the original fallback chain.
        const _booksCalc = Math.max(r?.booksCalculated || 0, r?.raw?.booksCalculated || 0);
        const globalCount = isSGP
          ? (Math.max(boKeys.length, _booksCalc) || 1)
          : (boKeys.length || _booksCalc || 1);
        let mbCount = boKeys.filter(isMyBook).length;
        // SGP items often have empty bookOdds — fall back to the top-level sportsbook field
        if (mbCount === 0 && isSGP) {
          const _sb = r?.raw?.sportsbook || r?.sportsbook || r?.raw?.book || r?.book || '';
          if (_sb && isMyBook(_sb)) mbCount = 1;
        }
        const threshold = isSGP ? minSGP : minBooks;
        if (!rawSeen.has(cardKey)) { rawCount++; rawSeen.add(cardKey); }
        if (!allSeen.has(cardKey) && globalCount >= threshold) { allCount++; allSeen.add(cardKey); }
        if (minMyBooks > 0 && !mySeen.has(cardKey) && globalCount >= threshold) {
          // SGPs: only 1 My Books book needed — you can only place a parlay at one book
          // anyway, and sgpOptimizer only queries ~5 books total via OpticOdds.
          const mbMin = isSGP ? Math.min(1, myBooks.length) : minMyBooks;
          if (mbCount >= mbMin) { myCount++; mySeen.add(cardKey); }
        }
      }
      return {
        ...q,
        hasMyBooks: true,
        allBooksCount: allCount,
        myBooksCount: myCount,
        rawResultCount: rawCount,
      };
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.preferences?.myBooks, activeQueries.length]);

  // Load query history from Firestore
  const loadQueryHistory = useCallback(async () => {
    if (historyLoadInFlight.current) return;
    const user = auth.currentUser;
    if (!user?.uid) {
      setHistoryLoading(false);
      return;
    }

    historyLoadInFlight.current = true;
    try {
      const q = query(
        collection(db, 'users', user.uid, 'query_history'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const history = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || new Date()
      }));
      setQueryHistory(history);
    } catch (error) {
      console.debug('[BackgroundQuery] Error loading history:', error);
    } finally {
      setHistoryLoading(false);
      historyLoadInFlight.current = false;
    }
  }, []);

  // Load history on mount and auth changes
  useEffect(() => {
    loadQueryHistory();
  }, [loadQueryHistory]);

  // Save query to history
  const saveToHistory = async (queryData) => {
    const user = auth.currentUser;
    if (!user?.uid) return;

    try {
      const historyEntry = {
        queryId: queryData.id,
        prompt: queryData.prompt,
        params: queryData.params,
        status: queryData.status,
        resultsCount: queryData.results?.length || 0,
        sgpCount: queryData.sgpCount || 0,
        durationMs: queryData.durationMs || 0,
        createdAt: serverTimestamp(),
        completedAt: queryData.status === QueryStatus.COMPLETED ? serverTimestamp() : null,
        cached: queryData.cached || false,
        error: queryData.error || null
      };

      await addDoc(collection(db, 'users', user.uid, 'query_history'), historyEntry);
      
      // Reload history
      await loadQueryHistory();
    } catch (error) {
      console.error('[BackgroundQuery] Error saving to history:', error);
    }
  };

  // Add a new background query
  const addBackgroundQuery = useCallback(async (prompt, params = {}, options = {}) => {
    // Check for duplicate queries (same prompt already processing)
    const { activeQueries, maxConcurrentQueries, processQuery } = latestRef.current;
    const normalizedPrompt = prompt?.trim()?.toLowerCase();
    const existingQuery = activeQueries.find(q => 
      q.prompt?.trim()?.toLowerCase() === normalizedPrompt &&
      (q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING)
    );
    
    if (existingQuery) {
      console.warn('[BackgroundQuery] Duplicate query detected, skipping:', prompt);
      return { 
        success: true, 
        queryId: existingQuery.id,
        duplicate: true 
      };
    }
    
    // Check if we've reached max concurrent queries
    const processingQueries = activeQueries.filter(
      q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
    );
    
    if (processingQueries.length >= maxConcurrentQueries) {
      console.warn('[BackgroundQuery] Max concurrent queries reached');
      return { 
        success: false, 
        error: `Maximum concurrent queries (${maxConcurrentQueries}) reached. Please wait for a query to complete.` 
      };
    }

    const queryId = generateQueryId();
    const startTime = Date.now();

    // Create new query object
    const newQuery = {
      id: queryId,
      prompt,
      params,
      status: QueryStatus.PENDING,
      progress: 0,
      progressMessage: 'Initializing...',
      results: null,
      error: null,
      startTime,
      cached: false,
      sgpCount: 0,
      durationMs: 0,
      // ⭐ Query type flags for UI badges (set from params at creation time)
      isLive: !!(params?.isLive || /\blive\b/i.test(prompt)),
      isSGPMode: !!(params?.mode === 'sgp' || params?.fromCachedSGP),
      fromTicker: !!(params?.fromTicker),
    };

    // Add to active queries
    setActiveQueries(prev => [...prev, newQuery]);

    // Start processing asynchronously
    processQuery(queryId, prompt, params, options);

    return { success: true, queryId };
  }, []);

  // Process a query
  //
  // Delegates to the unified `runSearchPipeline` so background and foreground
  // searches share one code path. This function now only handles:
  //   - mapping pipeline stages to the background UI progress model
  //   - updating activeQueries state
  //   - persisting to Firestore history
  //   - firing the completion notification
  const processQuery = async (queryId, prompt, params, options = {}) => {
    const startTime = Date.now();
    const queryMentionsSGP = /\bsgps?\b|parlays?|same\s*game/i.test(prompt);

    updateQueryState(queryId, {
      status: QueryStatus.PROCESSING,
      progress: 10,
      progressMessage: 'Checking cache...'
    });

    try {
      // ⭐ N2: background queries can't render clarification chips — suppress
      // the backend's clarify-or-abstain gate and take the legacy best guess.
      const pipelineResult = await runSearchPipeline(prompt, { ...params, noClarify: true }, {
        userProfile,
        onStatus: ({ stage, progress, progressMessage }) => {
          // Don't let a "done"/"sgp_cache_hit" stage overwrite our final
          // COMPLETED write below — we commit completion after this call returns.
          if (stage === 'done' || stage === 'sgp_cache_hit') return;
          updateQueryState(queryId, {
            progress: progress ?? 40,
            progressMessage: progressMessage || 'Processing...'
          });
        },
      });

      if (!pipelineResult.success) {
        throw new Error(pipelineResult.error || 'Query failed');
      }

      const {
        transformedResults,
        myBooksTransformed,
        sgpCount,
        isSGPResponse,
        _preferredSportsbook,
        _lineMetadata,
        _queryClassification,
        isLive,
        cached,
        durationMs,
        progressMessage,
        rawBody,
        referenceTime,
      } = pipelineResult;

      // ── Compute All Books / My Books counts for the background query card ──
      // Uses userProfile directly (not params, which doesn't carry defaults.myBooks
      // from the SgpFlow sendToBackground call). Thresholds mirror SgpFlowScreen
      // toggle badge logic (_allBooksCount / _myBooksCount useMemos).
      //
      // TWO INDEPENDENT counts (not subsets):
      //   allBooksCount = unique cards passing the global min-books threshold
      //   myBooksCount  = unique cards where ≥ min(minBooks, myBooks.length) of
      //                   the user's preferred books confirm the bet
      //
      // "Unique card" deduplication: alt lines (Over 27.5, Over 29.5 …) for the
      // same player/market/game collapse to ONE card, matching how the results
      // page groups them. Key = `${fixtureId}|${player}|${baseMarket}`.
      const _bgMyBooks = (userProfile?.preferences?.myBooks || []).map(b => (b || '').toLowerCase());
      const _bgMinBooks = parseInt(userProfile?.preferences?.minBooksStraights, 10) || 5;
      const _bgMinSGP   = parseInt(userProfile?.preferences?.minBooksSGP, 10) || 5;
      // My Books threshold: use the dedicated minBooksMyBooks pref (default 1).
      // Cap at myBooksList.length since you can't require more confirmations than you have books.
      const _bgUserMinMyBooks = parseInt(userProfile?.preferences?.minBooksMyBooks, 10) || 3;
      const _bgMinMyBooks = _bgMyBooks.length > 0 ? Math.min(_bgUserMinMyBooks, _bgMyBooks.length) : 0;
      const _bgIsMyBook = (bk) => {
        const bl = (bk || '').toLowerCase();
        return _bgMyBooks.some(mb => bl === mb || bl.includes(mb) || mb.includes(bl));
      };
      // Strip "Alt"/"Alternate"/"Over\/Under" suffix from market name (same as countUniqueCards)
      const _bgBaseMarket = (m) =>
        String(m || '').replace(/\s+(Alt|Alternate|Over\/Under)\b/gi, '').trim().toLowerCase();
      let _bgAllBooksCount = 0;
      let _bgMyBooksCount  = 0;
      // Raw count: unique cards regardless of min-books threshold.
      // Used to surface "X results below min books" when allBooksCount = 0.
      let _bgRawCount = 0;
      if (transformedResults?.length > 0) {
        const _bgAllSeen = new Set();
        const _bgMySeen  = new Set();
        const _bgRawSeen = new Set();
        for (const r of transformedResults) {
          if (r?._noOddsYet) continue;
          // Build card dedup key
          const isSGP = (r?.leg_count || r?.raw?.leg_count || 0) > 1 || !!r?._fromSGPCache;
          let cardKey;
          if (isSGP) {
            cardKey = `sgp:${r.id || r.raw?.id || Math.random()}`;
          } else {
            const sel  = r.raw?.selection || r.raw?.name || r.raw?.player_name || r.player_name || r.bet || '';
            const mkt  = _bgBaseMarket(r.market || r.raw?.market || '');
            const game = r.raw?.fixture_id || r.raw?.game || r.game || '';
            cardKey = `${game}|${sel}|${mkt}`;
          }
          const bo = r?.bookOdds || r?.raw?.bookOdds || {};
          const boKeys = Object.keys(bo);
          // ⭐ v1.43.36: max() across both sources — same logic as primary loop.
          const _booksCalcBg = Math.max(r?.booksCalculated || 0, r?.raw?.booksCalculated || 0);
          const globalCount = isSGP
            ? (Math.max(boKeys.length, _booksCalcBg) || 1)
            : (boKeys.length || _booksCalcBg || 1);
          // SGP cached items have booksCalculated but empty bookOdds — they carry a top-level
          // `sportsbook` field for which book the SGP was generated for. Fall back to that
          // so My Books count isn't always 0 for SGP queries.
          let myCount = boKeys.filter(_bgIsMyBook).length;
          if (myCount === 0 && isSGP) {
            const _sgpBook = r?.raw?.sportsbook || r?.sportsbook || r?.raw?.book || r?.book || '';
            if (_sgpBook && _bgIsMyBook(_sgpBook)) myCount = 1;
          }
          const minThreshold = isSGP ? _bgMinSGP : _bgMinBooks;
          // Raw count: every unique card, no threshold gate
          if (!_bgRawSeen.has(cardKey)) {
            _bgRawCount++;
            _bgRawSeen.add(cardKey);
          }
          // All Books: card passes global min-books threshold (count once per unique card)
          if (!_bgAllSeen.has(cardKey) && globalCount >= minThreshold) {
            _bgAllBooksCount++;
            _bgAllSeen.add(cardKey);
          }
          // My Books: card has ≥ minMyBooks of user's books AND passes the global
          // quality threshold. This ensures My Books count <= All Books count
          // (a card with 1-4 total books that happens to include a My Book should
          // not appear in My Books count if it doesn't pass the quality gate).
          if (_bgMinMyBooks > 0 && !_bgMySeen.has(cardKey) && globalCount >= minThreshold) {
            // SGPs: 1 My Books book is sufficient (parlay placed at one book only)
            const mbMin = isSGP ? Math.min(1, _bgMyBooks.length) : _bgMinMyBooks;
            if (myCount >= mbMin) {
              _bgMyBooksCount++;
              _bgMySeen.add(cardKey);
            }
          }
        }
      }

      // ── Focus-book count clamp ──────────────────────────────────────────────
      // When _preferredSportsbook is set (e.g. "caesars wnba sgps", "lakers fanduel"),
      // the results page will auto-apply it as a sportsbook filter. The displayed
      // counts MUST reflect only cards that book actually carries — otherwise we show
      // "150 All Books" but the user lands on a page with 0 visible results.
      //
      // Applies to ALL query types (SGP + straight). Previously this was gated on
      // isSGPResponse/queryMentionsSGP and skipped non-SGP cards, which caused the
      // mismatch for straight queries with a named book.
      const _normBgBook = (s) => String(s || '').toLowerCase()
        .replace(/\s*\([^)]*\)\s*$/, '').replace(/[^a-z0-9]+/g, '');
      const _bgFocusFp = _preferredSportsbook ? _normBgBook(_preferredSportsbook) : null;
      let _effectiveSgpCount = sgpCount;
      let _effectiveAllBooksCount = _bgAllBooksCount;
      let _effectiveMyBooksCount = _bgMyBooksCount;
      // Tracks whether to clear _preferredSportsbook when the focus book has 0 SGP coverage.
      let _clearFocusBook = false;

      if (_bgFocusFp && transformedResults?.length > 0) {
        const _focusAllSeen = new Set();
        const _focusMySeen  = new Set();
        let _focusSgpCount  = 0;
        for (const r of transformedResults) {
          if (r?._noOddsYet) continue;
          const isSGPCard = (r?.leg_count || r?.raw?.leg_count || 0) > 1 || !!r?._fromSGPCache;
          // Same dedup key logic as the _bgAllBooksCount loop above
          let cardKey;
          if (isSGPCard) {
            cardKey = `sgp:${r.id || r.raw?.id || Math.random()}`;
          } else {
            const sel  = r.raw?.selection || r.raw?.name || r.raw?.player_name || r.player_name || r.bet || '';
            const mkt  = _bgBaseMarket(r.market || r.raw?.market || '');
            const game = r.raw?.fixture_id || r.raw?.game || r.game || '';
            cardKey = `${game}|${sel}|${mkt}`;
          }
          const bo     = r?.bookOdds || r?.raw?.bookOdds || {};
          const boKeys = Object.keys(bo);
          // For SGP items: also check the top-level sportsbook field (empty bookOdds is common)
          const _sgpCardBook = isSGPCard ? (r?.raw?.sportsbook || r?.sportsbook || r?.raw?.book || r?.book || '') : '';
          const hasFocusBook = boKeys.some(bk => _normBgBook(bk) === _bgFocusFp)
            || (isSGPCard && _sgpCardBook && _normBgBook(_sgpCardBook) === _bgFocusFp);
          if (!hasFocusBook) continue;
          if (!_focusAllSeen.has(cardKey)) {
            _focusAllSeen.add(cardKey);
            if (isSGPCard) _focusSgpCount++;
            // My Books: focus book card also available via user's preferred books
            let myCount = boKeys.filter(_bgIsMyBook).length;
            if (myCount === 0 && isSGPCard && _sgpCardBook && _bgIsMyBook(_sgpCardBook)) myCount = 1;
            if (_bgMinMyBooks > 0 && myCount >= _bgMinMyBooks) {
              _focusMySeen.add(cardKey);
            }
          }
        }
        // ⭐ v1.43.37: If the focus book prices 0 SGPs but there ARE general SGP
        // results, fall back to the general count and clear _preferredSportsbook so
        // the results page doesn't auto-apply a dead sportsbook filter.
        // (e.g. "caesars mlb sgps" when Caesars doesn't price those parlays via OpticOdds)
        const _isSGPQuery = isSGPResponse || queryMentionsSGP;
        const _focusHasZeroSGPs = _isSGPQuery && _focusSgpCount === 0 && _bgAllBooksCount > 0;
        if (_focusHasZeroSGPs) {
          _clearFocusBook = true;
          _effectiveAllBooksCount = _bgAllBooksCount;
          _effectiveMyBooksCount  = _bgMyBooksCount;
          if (_isSGPQuery) _effectiveSgpCount = sgpCount;
        } else {
          _effectiveAllBooksCount = _focusAllSeen.size;
          _effectiveMyBooksCount  = _focusMySeen.size;
          // Only clamp sgpCount for SGP-flavoured queries so the non-SGP badge ("4 results")
          // isn't incorrectly zeroed out when the query doesn't mention SGPs.
          if (_isSGPQuery) _effectiveSgpCount = _focusSgpCount;
        }
      }

      // Card counts = the results page's counts on open (utils/resultCounts runs
      // the page's own filter pipeline). The loops above remain the fallback and
      // still decide the SGP focus-book clamp.
      let _pageCounts = null;
      try {
        _pageCounts = countBackgroundQuery({
          results: transformedResults,
          myBooksResults: myBooksTransformed,
          prompt,
          _queryClassification,
          _preferredSportsbook: _clearFocusBook ? null : _preferredSportsbook,
          referenceTime: referenceTime || rawBody?.meta?.reference_time || rawBody?.reference_time || null,
        }, userProfile, { hydrate: hydrateResultsForRender });
      } catch (countErr) {
        console.warn('[BackgroundQuery] page-count pipeline failed, using card counts:', countErr?.message);
      }

      updateQueryState(queryId, {
        status: QueryStatus.COMPLETED,
        progress: 100,
        progressMessage,
        results: transformedResults,
        myBooksResults: myBooksTransformed?.length > 0 ? myBooksTransformed : null,
        cached,
        sgpCount: _effectiveSgpCount,
        isSGPMode: !!(queryMentionsSGP || isSGPResponse),
        durationMs,
        _preferredSportsbook: _clearFocusBook ? null : _preferredSportsbook,
        _lineMetadata,
        _queryClassification,
        isLive,
        allBooksCount: _pageCounts ? _pageCounts.allBooksCount : _effectiveAllBooksCount,
        myBooksCount: _pageCounts ? _pageCounts.myBooksCount : _effectiveMyBooksCount,
        relaxMinBooks: _pageCounts ? _pageCounts.relaxMinBooks : undefined,
        allBooksCards: _pageCounts ? _pageCounts.allBooksCards : undefined,
        myBooksCards: _pageCounts ? _pageCounts.myBooksCards : undefined,
        hiddenText: _pageCounts ? _pageCounts.hiddenText : null,
        unreliableOnlyBooks: _pageCounts ? _pageCounts.unreliableOnlyBooks : null,
        _countsKey: _pageCounts ? countsKey(userProfile) : undefined,
        rawResultCount: _bgRawCount,
        hasMyBooks: _bgMyBooks.length > 0,
        // Cache diagnostics — used to explain zero results in the modal
        _cacheReason: rawBody?.sgp?.reason || null,
        // "No Caesars SGPs were captured — showing other books": the results page
        // shows this banner; View from Background must carry it too.
        _bookUnavailable: rawBody?._bookUnavailable || null,
        _message: rawBody?._message || null,
        _metaSource: rawBody?.meta?.source || rawBody?.source || null,
        referenceTime: referenceTime || rawBody?.meta?.reference_time || rawBody?.reference_time || null,
      });

      if (queryId && transformedResults?.length > 0) {
        const map = recentResultsMap.current;
        // Store top 100 by clean EV so feedback snapshots capture the best results.
        // transformedResults is already sorted by _sortEv (ev_percent_clean → ev_percent)
        // from transformSgpItems, so slice(0, 100) is the top 100.
        map.set(queryId, transformedResults.slice(0, 100));
        if (map.size > 50) {
          const firstKey = map.keys().next().value;
          map.delete(firstKey);
        }
      }

      await saveToHistory({
        id: queryId,
        prompt,
        params,
        status: QueryStatus.COMPLETED,
        results: transformedResults,
        cached,
        sgpCount,
        durationMs,
      });

      notifyQueryComplete(queryId, true);
      return;
    } catch (error) {
      console.error('[BackgroundQuery] Error processing query:', error);
      const durationMs = Date.now() - startTime;
      const rawMsg = error?.message || '';
      let friendlyError = rawMsg;
      if (/abort|timeout|timed?\s*out/i.test(rawMsg) || durationMs > 120000) {
        friendlyError = 'Request timed out — try again';
      } else if (/internal server error|status 500|500/i.test(rawMsg)) {
        friendlyError = 'Server error — backend may be overloaded, try again';
      } else if (/network|failed to fetch|load failed/i.test(rawMsg)) {
        friendlyError = 'Network error — check connection';
      } else if (/503|service unavailable/i.test(rawMsg)) {
        friendlyError = 'Server busy (503) — try again in a moment';
      }

      updateQueryState(queryId, {
        status: QueryStatus.ERROR,
        progress: 0,
        progressMessage: 'Query failed',
        isSGPMode: !!(queryMentionsSGP),
        error: friendlyError,
        durationMs,
      });

      await saveToHistory({
        id: queryId,
        prompt,
        params,
        status: QueryStatus.ERROR,
        error: rawMsg,
        durationMs,
      });

      notifyQueryComplete(queryId, false);
      return;
    }
  };

  // Update query state
  const updateQueryState = (queryId, updates) => {
    setActiveQueries(prev => prev.map(q => 
      q.id === queryId ? { ...q, ...updates } : q
    ));
  };

  // Play a subtle two-note ascending chime using the Web Audio API (no file needed)
  const playDoneSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      // E5 → G#5 — a soft ascending "done" chime
      const notes = [659.25, 830.61];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.18);
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.18);
        gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + i * 0.18 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.5);
        osc.start(ctx.currentTime + i * 0.18);
        osc.stop(ctx.currentTime + i * 0.18 + 0.5);
      });
      setTimeout(() => ctx.close().catch(() => {}), 1200);
    } catch (e) {
      console.warn('[BackgroundQuery] Sound error:', e);
    }
  };

  // Notify query completion
  const notifyQueryComplete = (queryId, success) => {
    if (Platform.OS === 'web') {
      try {
        // Subtle done sound (opt-in preference)
        if (success && userProfile?.preferences?.doneSoundEnabled) {
          playDoneSound();
        }

        // Browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          const query = activeQueries.find(q => q.id === queryId);
          new Notification('Query Complete', {
            body: success 
              ? `Found ${query?.results?.length || 0} results for "${query?.prompt?.slice(0, 30)}..."` 
              : 'Query failed',
            icon: '/favicon.ico'
          });
        }
        
        // Dispatch custom event for UI notification
        window.dispatchEvent(new CustomEvent('backgroundQueryComplete', {
          detail: { queryId, success }
        }));
      } catch (e) {
        console.warn('[BackgroundQuery] Notification error:', e);
      }
    }
  };

  // Rerun an errored (or cancelled) query in place — reuses the same card/id,
  // resets state back to pending, and re-invokes processQuery with the original
  // prompt/params. Guarded against retrying a query that's still running.
  const retryQuery = useCallback((queryId) => {
    const existing = activeQueries.find(q => q.id === queryId);
    if (!existing) {
      console.warn('[BackgroundQuery] retryQuery: query not found', queryId);
      return;
    }
    if (existing.status === QueryStatus.PENDING || existing.status === QueryStatus.PROCESSING) {
      console.warn('[BackgroundQuery] retryQuery: query already running', queryId);
      return;
    }

    const freshStart = Date.now();
    setActiveQueries(prev => prev.map(q =>
      q.id === queryId
        ? {
            ...q,
            status: QueryStatus.PENDING,
            progress: 0,
            progressMessage: 'Retrying…',
            results: null,
            error: null,
            startTime: freshStart,
            durationMs: 0,
            cached: false,
            sgpCount: 0,
          }
        : q
    ));

    processQuery(queryId, existing.prompt, existing.params || {});
  }, [activeQueries]);

  // Cancel a query
  const cancelQuery = useCallback((queryId) => {
    setActiveQueries(prev => prev.map(q => 
      q.id === queryId && (q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING)
        ? { ...q, status: QueryStatus.CANCELLED, progressMessage: 'Cancelled' }
        : q
    ));
  }, []);

  // Remove a query from active list
  const removeQuery = useCallback((queryId) => {
    setActiveQueries(prev => prev.filter(q => q.id !== queryId));
  }, []);

  // Mark a completed query as viewed
  const markQueryViewed = useCallback((queryId) => {
    setViewedCompletedQueries(prev => new Set([...prev, queryId]));
  }, []);

  // Get unviewed completed queries count
  const getUnviewedCount = useCallback(() => {
    return activeQueries.filter(
      q => q.status === QueryStatus.COMPLETED && !viewedCompletedQueries.has(q.id)
    ).length;
  }, [activeQueries, viewedCompletedQueries]);

  // Clear all completed/cancelled queries
  const clearCompletedQueries = useCallback(() => {
    setActiveQueries(prev => prev.filter(
      q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
    ));
  }, []);

  // Delete a query from history
  const deleteFromHistory = async (historyId) => {
    const user = auth.currentUser;
    if (!user?.uid) return;

    try {
      await deleteDoc(doc(db, 'users', user.uid, 'query_history', historyId));
      setQueryHistory(prev => prev.filter(h => h.id !== historyId));
    } catch (error) {
      console.error('[BackgroundQuery] Error deleting from history:', error);
    }
  };

  // Clear all history
  const clearHistory = async () => {
    const user = auth.currentUser;
    if (!user?.uid) return;

    try {
      const q = query(collection(db, 'users', user.uid, 'query_history'));
      const snapshot = await getDocs(q);
      
      const deletePromises = snapshot.docs.map(d => 
        deleteDoc(doc(db, 'users', user.uid, 'query_history', d.id))
      );
      
      await Promise.all(deletePromises);
      setQueryHistory([]);
    } catch (error) {
      console.error('[BackgroundQuery] Error clearing history:', error);
    }
  };

  // Check if any queries are currently processing
  const hasProcessingQueries = activeQueries.some(
    q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
  );

  // Get the number of active (non-completed) queries
  const activeQueryCount = activeQueries.filter(
    q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
  ).length;

  // Toggle modal position between bottom-right and bottom-left
  const toggleModalPosition = useCallback(() => {
    setModalPosition(prev => prev === 'bottom-right' ? 'bottom-left' : 'bottom-right');
  }, []);

  latestRef.current = { activeQueries, maxConcurrentQueries, processQuery, saveToHistory };

  // Look up a query by id (results-page handoff for "View").
  const getQueryById = useCallback(
    (id) => (id ? latestRef.current.activeQueries.find((q) => q.id === id) || null : null),
    []
  );

  // Register a foreground query (called from SgpFlowScreen when starting a query)
  // This allows us to track it and convert to background if user navigates away
  const registerForegroundQuery = useCallback((prompt, params, progressCallback) => {
    const queryId = generateQueryId();
    setForeground({
      id: queryId,
      prompt,
      params,
      status: QueryStatus.PROCESSING,
      progress: 0,
      progressMessage: 'Processing...',
      startTime: Date.now(),
      progressCallback
    });
    return queryId;
  }, []);

  // Clear foreground query (called when query completes in foreground)
  const clearForegroundQuery = useCallback(() => {
    setForeground(null);
  }, []);
  
  // ⭐ Complete foreground query and save to history
  const completeForegroundQuery = useCallback(async (results, options = {}) => {
    const fq = foregroundQueryRef.current;
    if (!fq) {
      console.log('[BackgroundQuery] No foreground query to complete');
      return;
    }
    // An older search finishing late (the user left and started another)
    // must not save its results under the newer search or clear it.
    if (options.queryId && fq.id !== options.queryId) {
      console.log('[BackgroundQuery] Stale foreground completion ignored:', options.queryId);
      return;
    }
    
    const { id, prompt, params, startTime } = fq;
    const durationMs = Date.now() - (startTime || Date.now());
    
    // Save to history
    await latestRef.current.saveToHistory({
      id,
      prompt,
      params,
      status: QueryStatus.COMPLETED,
      results: results || [],
      cached: options.cached || false,
      sgpCount: options.sgpCount || results?.length || 0,
      durationMs
    });
    
    console.log('[BackgroundQuery] ✅ Foreground query saved to history:', { prompt, resultsCount: results?.length || 0 });
    
    // Clear foreground query
    setForeground(null);
  }, []);

  // Convert foreground query to background (called when user navigates away)
  // `scopeParams` (league / sport / prefer / fixtureId from the caller) are kept
  // on top of the foreground's own params so a fixture-scoped search stays scoped.
  const convertToBackground = useCallback(async (scopeParams = {}) => {
    const fq = foregroundQueryRef.current;
    if (!fq) {
      return { success: false, error: 'No foreground query to convert' };
    }

    const { activeQueries, maxConcurrentQueries, processQuery } = latestRef.current;
    const { id, prompt, progress, progressMessage, startTime } = fq;
    const params = { ...(fq.params || {}), ...(scopeParams || {}) };
    
    // Skip only if the same query is already running in background. A
    // COMPLETED query with the same prompt must not block a re-run — that
    // silently dropped repeat searches (e.g. a second "Liberty SGP").
    const normalizedPrompt = prompt?.trim()?.toLowerCase();
    const alreadyExists = activeQueries.some(q => 
      q.id === id || (
        q.prompt?.trim()?.toLowerCase() === normalizedPrompt &&
        (q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING)
      )
    );
    
    if (alreadyExists) {
      console.log('[BackgroundQuery] Query already exists in background, skipping conversion');
      setForeground(null);
      return { success: true, queryId: id, duplicate: true };
    }

    // Check if we've reached max concurrent queries
    const processingQueries = activeQueries.filter(
      q => q.status === QueryStatus.PENDING || q.status === QueryStatus.PROCESSING
    );
    
    if (processingQueries.length >= maxConcurrentQueries) {
      return { 
        success: false, 
        error: `Maximum concurrent background queries (${maxConcurrentQueries}) reached` 
      };
    }

    // Create background query from foreground state
    const backgroundQuery = {
      id,
      prompt,
      params,
      status: QueryStatus.PROCESSING,
      progress: progress || 10,
      progressMessage: progressMessage || 'Processing in background...',
      results: null,
      error: null,
      startTime,
      cached: false,
      sgpCount: 0,
      durationMs: 0,
      convertedFromForeground: true
    };

    // Add to active queries
    setActiveQueries(prev => [...prev, backgroundQuery]);
    
    // Clear foreground query
    setForeground(null);
    
    // Expand the modal so user can see it
    setIsMinimized(false);

    // Continue processing in background
    processQuery(id, prompt, params);

    return { success: true, queryId: id };
  }, []);

  // Send current query directly to background (user clicks "Send to Background" button)
  const sendToBackground = useCallback(async (prompt, params) => {
    // First clear the foreground query if it matches
    if (foregroundQueryRef.current?.prompt === prompt) {
      return convertToBackground(params);
    }
    
    // Otherwise start a new background query
    return addBackgroundQuery(prompt, params);
  }, [convertToBackground, addBackgroundQuery]);

  // Check if we can add more background queries
  const canAddBackgroundQuery = activeQueryCount < maxConcurrentQueries;

  return (
    <BackgroundQueryContext.Provider value={{
      // State
      activeQueries,
      queryHistory,
      historyLoading,
      isMinimized,
      hasProcessingQueries,
      activeQueryCount,
      modalPosition,
      isMobileHidden,
      foregroundQuery,
      canAddBackgroundQuery,
      
      // Actions
      addBackgroundQuery,
      cancelQuery,
      retryQuery,
      removeQuery,
      markQueryViewed,
      getUnviewedCount,
      clearCompletedQueries,
      setIsMinimized,
      toggleModalPosition,
      setIsMobileHidden,
      
      // Foreground-to-background conversion
      registerForegroundQuery,
      clearForegroundQuery,
      completeForegroundQuery,
      convertToBackground,
      sendToBackground,
      getQueryById,
      
      // History actions
      loadQueryHistory,
      deleteFromHistory,
      clearHistory,

      // In-memory results map (Map<queryId, result[]>, top 20 per query)
      recentResultsMap: recentResultsMap.current,
      
      // Constants
      maxConcurrentQueries,
      QueryStatus,
      IS_MOBILE
    }}>
      {children}
    </BackgroundQueryContext.Provider>
  );
}

export function useBackgroundQuery() {
  const context = useContext(BackgroundQueryContext);
  if (context === undefined) {
    throw new Error('useBackgroundQuery must be used within a BackgroundQueryProvider');
  }
  return context;
}

